<template>
  <Card :class="$style.card">
    <div
      :class="$style.formContainer"
    >
      <div v-if="errorMessage">
        <HeadingLarge :class="$style.heading">
          {{ errorHeaderText }}
        </HeadingLarge>
        <ParagraphBody
          :class="$style.error"
          data-test-id="AdminGoogleRegistrationSignup_errorMessage"
        >
          {{ errorMessage }}
        </ParagraphBody>
        <figure :class="$style.illustrationContainer">
          <HeadOnDeskIllustrationImg :class="$style.illustration" />
        </figure>
        <Button
          ref="cancelButton"
          :class="$style.button"
          hasOutline
          isMarketingStyle
          text="cancel"
          @click="handleCancelClick"
        />
        <Button
          ref="retryButton"
          :class="$style.button"
          :isLoading="loading"
          isMarketingStyle
          text="retry"
          @click="submitRequest"
        />
        <ParagraphBody
          bodySize="small"
          :class="$style.supportText"
        >
          Still having trouble? Contact
          <a
            href="https://jumpcloud.com/support"
            rel="noopener noreferrer"
            target="_blank"
          >
            JumpCloud Support
          </a>
          for help.
        </ParagraphBody>
      </div>
      <div v-else>
        <HeadingLarge :class="$style.heading">
          {{ createNewOrg }}
        </HeadingLarge>
        <ParagraphBody bodySize="large">
          {{ subHeaderCreateText }}
        </ParagraphBody>
        <ParagraphBody bodySize="small">
          <LoadingIcon
            :class="$style.loading"
            icon="loading"
            :inline="true"
            :scale="2"
          />
          This should only take a moment
        </ParagraphBody>
        <figure :class="[$style.illustrationContainer, $style.loadingIllustrationContainer]">
          <ConfirmationIllustrationCodeImg
            :class="[$style.illustration, $style.loadingIllustration]"
          />
        </figure>
      </div>
    </div>
  </Card>
</template>

<script>
import {
  Button,
  Card,
  HeadingLarge,
  LoadingIcon,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import { RouteGenerator, SignupConsts } from 'client/util/Constants';
import { getUtmFields } from 'client/signup/store/signupStoreConfig';
import { mapActions, mapGetters } from 'vuex';
import ConfirmationIllustrationCodeImg from 'img/signup/confirmation-illustration-code.svg';
import Cookies from 'js-cookie';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import HeadOnDeskIllustrationImg from 'img/signup/head-on-desk-illustration.svg';
import SignupApi from 'client/signup/api/SignupApi';
import Util from 'client/util/Util';
import get from 'lodash/get';

const { adminGoogleRegistrationError } = SignupConsts.responseMessages;
const { adminPortalLandingPage } = RouteGenerator;
const { createNewOrg, errorHeaderText, subHeaderCreateText } = SignupConsts.SignupText;
const {
  buttonTypes,
  clientType,
  clientTypeCookie,
  gtmEvents,
  thirdPartySources,
  useCaseValues,
} = SignupConsts;

const { admin } = clientType;

export default {
  name: 'AdminGoogleRegistrationSignup',

  components: {
    Button,
    Card,
    ConfirmationIllustrationCodeImg,
    HeadOnDeskIllustrationImg,
    HeadingLarge,
    LoadingIcon,
    ParagraphBody,
  },

  props: {
    redirectLocation: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      errorMessage: '',
      createNewOrg,
      emailAddress: '',
      errorHeaderText,
      subHeaderCreateText,
    };
  },

  computed: {
    ...mapGetters([
      'getGrowthData',
    ]),

    errorButtonType() { return buttonTypes.error; },

    eventParams() {
      return {
        email: this.emailAddress,
        ...thirdPartySources.google,
      };
    },

  },

  mounted() {
    this.getQueries();
    this.submitRequest();
  },

  methods: {
    ...mapActions(['clearUtmValues']),

    getQueries() {
      const { query } = this.$route;
      // The email address should be passed in the query
      this.emailAddress = query.email;
    },

    handleCancelClick() {
      EventHelper.triggerSignupCancelled(this.eventParams);
      Util.setLocation(this.redirectLocation);
    },

    async request() {
      const payload = {
        growthData: this.getGrowthData,
        Double_Opt_In__c: false,
        Use_Case__c: useCaseValues.notProvided,
      };

      const utmData = getUtmFields();
      await EventHelper.triggerSignupSubmitted({ ...this.eventParams, ...utmData });

      SignupApi.newOrgVerification(payload)
        .then(async () => {
          try {
            if (window.dataLayer) {
              window.dataLayer.push(gtmEvents.googleTrackingEvent);
              window.dataLayer.push(gtmEvents.freeAccountTrackingEvent);
              window.dataLayer.push(gtmEvents.accountVerified);
            }

            await EventHelper.triggerAccountCreated(this.eventParams);
            await EventHelper.triggerAccountVerified(this.eventParams);
          } catch (e) {
            // Do nothing, just catch for safety and move on
          }
        })
        .then(() => {
          try {
            // only clear utm values if we succeeded
            this.clearUtmValues();
            Cookies.set(clientTypeCookie, admin);
          } catch (e) {
            // Do nothing, just catch for safety and move on
          }

          // verification was successful and we're logged in
          // redirect to the admin portal
          Util.setLocation(adminPortalLandingPage);
        })
        .catch((error) => {
          const apiError = get(error, 'response.data', {});
          EventHelper.triggerAccountCreateFailed({
            errorCode: apiError,
            ...thirdPartySources.google,
          });
          this.errorMessage = adminGoogleRegistrationError;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    submitRequest() {
      this.errorMessage = '';
      this.loading = true;
      setTimeout(() => this.request(), 1000);
    },
  },
};
</script>

<style module>
@value (max-width-medium) from '~css/breakpoints.css';

.card.card {
  box-shadow: var(--jc-box-shadow-card);
  display: grid;
  grid-template-columns: 1fr;
  margin: auto;
  padding: 0;
  width: 70rem;
  z-index: 1;
}

.error {
  margin-bottom: 2rem;
}

.formContainer {
  min-height: 42rem;
  padding: 4rem 1.5rem 0;
  text-align: center;
}

.loading {
  color: var(--jcPrimary);
}

.button {
  margin: 0 1rem 1.5rem 0;
  width: 15%;
}

.illustrationContainer {
  display: flex;
  flex-direction: column;
  grid-area: image;
  height: fit-content;
  justify-content: flex-end;
  margin: 0;
}

.loadingIllustrationContainer {
  padding-bottom: 1.5rem;
}

.illustration {
  align-self: center;
  height: 100%;
  max-height: 32vh;
  min-height: 25rem;
  width: auto;
}

.loadingIllustration {
  max-width: 65%;
}

.heading {
  font-family: var(--serifFont);
}

.supportText.supportText {
  margin: 0 0 1.5rem;
}

@media (max-width: max-width-medium) {
  .card.card {
    display: block;
    margin: 0;
    min-height: 100%;
    min-width: 100%;
  }

  .button {
    margin-top: 35%;
  }

  .formContainer {
    padding: 3rem 1.5rem 0;
  }
}
</style>
